import { useDeleteMutation, useGetQuery, useGetQueryCollection, usePostMutation, usePutMutation } from 'shared/lib/api';
import { pathToUrl } from 'shared/lib/router';
import { useDownloadFile } from 'shared/lib/hooks';
import type { DeepPartial } from 'utility-types';
import { createQueryKeys } from '@lukemorales/query-key-factory';

import {
  type CampaignGroupingDetailsModel,
  type CampaignGroupsCounters,
  type CampaignInGroupingModel,
  type CreateGroupModel,
  type GroupModel,
  type ValidateZipsModel,
} from '../model';

import { routes } from './routes';

export const useAddAllCampaignToGroup = (groupId: string, urlFilters?: string) =>
  usePutMutation(pathToUrl(routes.internal.addAllCampaignsInGroup, { groupId }), {
    urlFilters,
  });
export const useDeleteAllCampaignFromGroup = (groupId: string) =>
  useDeleteMutation(
    pathToUrl(routes.internal.deleteAllCampaignsInGroup, {
      groupId,
    })
  );

export const useDownloadGroupsZipCSV = () => useDownloadFile({ url: pathToUrl(routes.internal.getGroupsZipCSV) });

const campaignsInGroupsKeys = createQueryKeys('campaignsInGroups ', {
  campaignsInGroupsList: [''],
});

export const useGetCampaignsInGrouping = ({
  groupId,
  urlFilters,
  enabled,
}: {
  groupId: string;
  urlFilters?: string;
  enabled?: boolean;
}) => {
  return useGetQueryCollection<CampaignInGroupingModel>(routes.internal.getCampaignsInGrouping, {
    queryKey: [campaignsInGroupsKeys, urlFilters],
    urlParams: { groupId },
    urlFilters,
    enabled,
    refetchOnMount: false,
    keepPreviousData: true,
  });
};

export const useAddCampaignToGroup = (groupId: string, campaignId: string) =>
  usePutMutation(pathToUrl(routes.internal.addCampaignInGroup, { groupId, campaignId }));

export const useDeleteCampaignFromGroup = (groupId: string, campaignId: string) =>
  useDeleteMutation(
    pathToUrl(routes.internal.deleteCampaignFromGroup, {
      groupId,
      campaignId,
    })
  );

export const usePublishGroup = () => usePutMutation(routes.internal.publishGroup);

export const useGetSingleGroupCSV = () => useDownloadFile({ url: routes.internal.getSingleGroupCSV, type: 'zip' });

export const useGetSingleCampaignCSV = () => useDownloadFile({ url: routes.internal.getSingleCampaignCSV });

export const useCreateGroup = () =>
  usePostMutation<Omit<CreateGroupModel, 'status'>, { id: number }>(routes.internal.createGroup);

const groupsKeys = createQueryKeys('campaignsGroups ', {
  campaignsGroupsList: [''],
});

export const useGetGroups = ({ urlFilters, enabled }: { urlFilters?: string; enabled?: boolean }) =>
  useGetQueryCollection<GroupModel>(routes.internal.getAllGroups, {
    queryKey: [groupsKeys, urlFilters],
    urlFilters,
    enabled,
    keepPreviousData: true,
    refetchOnMount: false,
  });

const groupsDetailsKeys = createQueryKeys('groupsDetails', {
  groupsDetailsList: [''],
});

export const useGetCampaignGroupingDetails = ({
  groupId,
  urlFilters,
  enabled,
}: {
  groupId: string | number;
  urlFilters?: string;
  enabled?: boolean;
}) =>
  useGetQueryCollection<CampaignGroupingDetailsModel>(routes.internal.getCampaignGroupingDetails, {
    queryKey: [groupsDetailsKeys.groupsDetailsList.queryKey, urlFilters],
    urlParams: { id: groupId },
    urlFilters,
    enabled,
    keepPreviousData: true,
    refetchOnMount: false,
  });

export const useUpdateGroup = (id: string | number) =>
  usePutMutation<DeepPartial<CreateGroupModel>, { id: number }>(
    pathToUrl(routes.internal.updateGroup, {
      groupId: id,
    })
  );

const singleGroupKeys = createQueryKeys('singleGroup', {
  singleGroupList: [''],
});

export const useSingleGroup = (id: string | number, enabled = true) =>
  useGetQuery<GroupModel>(pathToUrl(routes.internal.getSingleGroup, { id }), {
    enabled,
    queryKey: [singleGroupKeys, id],
  });

export const useDeleteDraftGroup = () => useDeleteMutation(routes.internal.deleteDraftGroup);

export const useCampaignGroupsCounters = () =>
  useGetQuery<CampaignGroupsCounters>(routes.internal.groupsCounters, { enabled: true, keepPreviousData: true });

export const useZipValidation = () =>
  usePostMutation<{ zipCodes: string }, ValidateZipsModel>(routes.internal.zipValidation);

export function useGetCampaignGroups(id: number, enabled = true) {
  return useGetQuery<GroupModel[]>(routes.internal.getCampaignGroups, {
    urlParams: { id },
    enabled,
  });
}
